import { ProDescriptions } from "@ant-design/pro-components";
import { Link } from "react-router-dom";
import { Tag } from "antd";
import { useTranslation } from "react-i18next";
import { useLiftRbes } from "@/hooks";
import { isArray } from "lodash-es";
import { useMemo } from "react";
import {
  PageEnum,
  LIFT_ALARM_TABLE_HEADER,
  TABLEPAI,
  COL1,
  COL2,
  REB_TITLE_CODE,
} from "@/enums/pageEnum";
import { CustomDetailPage, HView, ComingSoon } from "@/components";
import AlarmComponet from "@/pages/lmd/detail/AlarmComponet";
import { TelemetryItem } from "@/facilities/model/user";

export default function LiftDetailPage() {
  const { t } = useTranslation();
  const INFO = "#INFO";
  const { rebTempers } = useLiftRbes();
  const rebsList = useMemo(() => {
    const keys: string[] = [];
    if (isArray(rebTempers)) {
      rebTempers.forEach((item: TelemetryItem) => {
        if (item.value === "true") {
          keys.push(item.key);
        }
      });
    }
    return REB_TITLE_CODE.filter((item) => keys.includes(item));
  }, [rebTempers]);

  const buildColumns = useMemo(() => {
    return [
      { title: t("ward"), dataIndex: "ward", ...COL1 },
      { title: t("address"), dataIndex: "address", ...COL2 },
      {
        title: t("building"),
        dataIndex: "name",
        ...COL1,
        renderText: (_: string, record: { id: string }) => (
          <HView
            name={_}
            path={`${PageEnum.BUILDING}/detail/${record?.id}`}
            state={record}
            isNewWindow
          />
        ),
      },
      { title: t("buildingSegment"), dataIndex: "buildingSegmented" },
    ];
  }, [t]);

  const liftInfoColumns = useMemo(() => {
    return [
      { title: t("liftId"), dataIndex: "id", ...COL1 },
      { title: t("brand"), dataIndex: "brand", ...COL2 },
      { title: t("maCompany"), dataIndex: "maintenanceCompany", ...COL1 },
      { title: t("liftNo"), dataIndex: "lift" },
      { title: t("contractTerm"), dataIndex: "contractTerm" },
      { title: t("turnOnDate"), dataIndex: "turnOnDate" },
      { title: t("expiryOfCertificate"), dataIndex: "expiryOfCertificate" },
      { title: t("propertyManager"), dataIndex: "propertyManager" },
      { title: t("mRLocation"), dataIndex: "mRLocation" },
      { title: t("waterTankAboveMR"), dataIndex: "waterTankAboveMR" },
      { title: t("hoistwayPosition"), dataIndex: "hoistwayPosition" },
      { title: t("landingType"), dataIndex: "landingType" },
      { title: t("storey"), dataIndex: "storey" },
      { title: t("landingLocation"), dataIndex: "landingLocation" },
      { title: t("noOfLandings"), dataIndex: "noOfLandings" },
    ];
  }, [t]);

  const liftDriveColumns = useMemo(() => {
    return [
      { title: t("peCapPax"), dataIndex: "personCapacityPax", ...COL1 },
      { title: t("loadCapacitykgs"), dataIndex: "loadCapacityKgs", ...COL2 },
      { title: t("loadTestDate"), dataIndex: "loadTestDate", ...COL1 },
      { title: t("driveType"), dataIndex: "driveType" },
      { title: t("liftType"), dataIndex: "liftType" },
      { title: t("speedMMin"), dataIndex: "speedMMin" },
      { title: t("carControl1"), dataIndex: "carControl1" },
      { title: t("carControl2"), dataIndex: "carControl2", span: 2 },
      { title: t("upgradeCat"), dataIndex: "upgradeCat" },
      { title: t("upgradeBatch"), dataIndex: "upgradeBatch" },
      { title: t("upgradeType"), dataIndex: "upgradeType" },
    ];
  }, [t]);

  const deviceColumns = useMemo(() => {
    return [
      { title: t("lssInstalledDate"), dataIndex: "lssInstalledDate", ...COL1 },
      { title: t("ardBrand"), dataIndex: "ardBrand", ...COL2 },
      { title: t("ardDate"), dataIndex: "ardCommissionDate", ...COL1 },
    ];
  }, [t]);

  const lmdColumns = useMemo(() => {
    return [
      {
        title: t("lmd"),
        dataIndex: "id",
        ...COL1,
        renderText: (_: string, record: { name: string }) => (
          <HView
            name={record?.name}
            path={`${PageEnum.LMD}/detail/${_}`}
            state={record}
            isNewWindow
          />
        ),
      },
      { title: t("lmdBrand"), dataIndex: "brand", ...COL2 },
      { title: t("lmdInstallDate"), dataIndex: "lmdInstallDate", ...COL1 },
    ];
  }, [t]);

  const tabList = useMemo(() => {
    return [
      { tab: t("info"), key: INFO },
      { tab: t("alarm"), key: "#ALARM" },
      { tab: t("mainteRecord"), key: "#MAINTERECORD" },
    ];
  }, [t]);

  return (
    <CustomDetailPage
      tabList={tabList}
      url="lift"
      funcChildren={(key: string, liftDatas: any) => {
        const { attribute, building, lmd } = liftDatas;
        if (key === INFO) {
          return (
            <div className="bg-[#fff] dark:bg-[#141414] min-h-[calc(100vh-297px)] p-4">
              <ProDescriptions
                bordered
                dataSource={{ ...building?.attribute, ...building }}
                title={t("building")}
                columns={buildColumns}
              />
              <ProDescriptions
                bordered
                dataSource={{ ...attribute, ...liftDatas }}
                className="mt-4"
                title={`${t("lift")} ${t("info")}`}
                columns={liftInfoColumns}
              />
              <ProDescriptions
                bordered
                className="mt-4"
                dataSource={{ ...attribute, ...liftDatas }}
                columns={liftDriveColumns}
              />
              <ProDescriptions
                bordered
                className="mt-4"
                dataSource={{ ...lmd?.attribute, ...lmd }}
                title={t("relatedDevice")}
                columns={lmdColumns}
              />
              <ProDescriptions
                bordered
                dataSource={attribute}
                columns={deviceColumns}
              />
            </div>
          );
        } else if (key === "#ALARM") {
          return (
            <AlarmComponet
              editTableHeaderUrl={LIFT_ALARM_TABLE_HEADER}
              isLiftlink={false}
              rbeApiCode={`${TABLEPAI.RBE_CODE}?view=LIFT_SELECTOR`}
            />
          );
        }
        return (
          <div className="bg-[#fff] dark:bg-[#141414]">
            <ComingSoon />
          </div>
        );
      }}
      headerCongif={(_: string, liftDatas: any) => ({
        title: (
          <span className="text-[20px] font-medium">{liftDatas?.name}</span>
        ),
        subTitle: (
          <>
            {rebsList.map((item) => (
              <Tag key={item} color="#f50">
                {item}
              </Tag>
            ))}
          </>
        ),
        children: (
          <div className="mb-5">
            <span className="mr-10">
              {t("lMDID")}:{" "}
              {liftDatas?.lmd && (
                <HView
                  name={liftDatas.lmd?.name}
                  path={`${PageEnum.LMD}/detail/${liftDatas?.lmd?.id}`}
                  state={{ ...liftDatas?.lmd }}
                  isNewWindow
                />
              )}
            </span>
            <span className="mr-10">
              {t("building")}:{" "}
              {liftDatas?.building && (
                <HView
                  name={liftDatas?.building?.name}
                  path={`${PageEnum.BUILDING}/detail/${liftDatas?.building?.id}`}
                  state={{ ...liftDatas?.building }}
                  isNewWindow
                />
              )}
            </span>
            <span className="mr-10">
              {t("brand")}: {liftDatas?.attribute?.brand}
            </span>
            <span className="mr-10">
              {t("propertyManage")}: {liftDatas?.attribute?.propertyManager}
            </span>
            <span>
              {t("maintenanceCompany")}:{" "}
              {liftDatas?.attribute?.maintenanceCompany}
            </span>
          </div>
        ),
        breadcrumb: {
          items: [
            { title: <Link to={PageEnum.LIFT}>{t("lift")}</Link> },
            { title: t("detail") },
          ],
        },
      })}
    />
  );
}
